import { useId, useMemo } from 'react'
import { cn } from '@/lib/utils'

interface AreaChartProps {
  data: number[]
  className?: string
  height?: number
  strokeColor?: string
  fillColorStart?: string
  fillColorEnd?: string
  animate?: boolean
}

/**
 * Attempt monotone cubic interpolation for a smooth SVG path.
 * Given data points equally spaced on the x-axis, returns an SVG path `d` string.
 */
function buildSmoothPath(
  data: number[],
  width: number,
  height: number,
  padding: number,
): { linePath: string; areaPath: string } {
  if (data.length < 2) return { linePath: '', areaPath: '' }

  const max = Math.max(...data)
  const min = Math.min(...data)
  const range = max - min || 1

  const plotW = width - padding * 2
  const plotH = height - padding * 2

  const points = data.map((v, i) => ({
    x: padding + (i / (data.length - 1)) * plotW,
    y: padding + plotH - ((v - min) / range) * plotH,
  }))

  // Build cubic bezier segments using Catmull-Rom → cubic bezier conversion
  const segments: string[] = [`M ${points[0].x},${points[0].y}`]

  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[Math.max(0, i - 1)]
    const p1 = points[i]
    const p2 = points[i + 1]
    const p3 = points[Math.min(points.length - 1, i + 2)]

    const tension = 0.35
    const cp1x = p1.x + (p2.x - p0.x) * tension
    const cp1y = p1.y + (p2.y - p0.y) * tension
    const cp2x = p2.x - (p3.x - p1.x) * tension
    const cp2y = p2.y - (p3.y - p1.y) * tension

    segments.push(`C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`)
  }

  const linePath = segments.join(' ')

  // Close the area path along the bottom
  const lastPoint = points[points.length - 1]
  const firstPoint = points[0]
  const areaPath = `${linePath} L ${lastPoint.x},${height} L ${firstPoint.x},${height} Z`

  return { linePath, areaPath }
}

export function AreaChart({
  data,
  className,
  height = 140,
  strokeColor = 'url(#line-gradient)',
  fillColorStart = 'var(--chart-fill-start)',
  fillColorEnd = 'var(--chart-fill-end)',
  animate = true,
}: AreaChartProps) {
  const gradientId = useId()
  const fillGradientId = `fill-${gradientId}`
  const lineGradientId = `line-${gradientId}`

  const width = 400
  const padding = 4

  const { linePath, areaPath } = useMemo(
    () => buildSmoothPath(data, width, height, padding),
    [data, width, height],
  )

  if (!linePath) return null

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className={cn('w-full', className)}
      style={{ height }}
    >
      <defs>
        <linearGradient id={fillGradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={fillColorStart} />
          <stop offset="100%" stopColor={fillColorEnd} />
        </linearGradient>
        <linearGradient id={lineGradientId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="var(--chart-line)" />
          <stop offset="50%" stopColor="var(--chart-line-alt)" />
          <stop offset="100%" stopColor="var(--chart-line)" />
        </linearGradient>
      </defs>

      {/* Area fill */}
      <path
        d={areaPath}
        fill={`url(#${fillGradientId})`}
        className={animate ? 'animate-[chart-fade-in_1.2s_ease-out_both]' : undefined}
      />

      {/* Line stroke — 1px */}
      <path
        d={linePath}
        fill="none"
        stroke={
          strokeColor === 'url(#line-gradient)' ? `url(#${lineGradientId})` : strokeColor
        }
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={animate ? 'animate-[chart-draw_1.4s_ease-out_both]' : undefined}
        pathLength={1}
      />
    </svg>
  )
}
